import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Clock, Mail, MapPin, MessageCircle, Phone, Send } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Field, Input, Textarea } from '@/components/ui/Input';
import { Nav, Footer } from './LandingPage';

/**
 * Halaman Hubungi Kami.
 *
 * ── Kenapa halaman tersendiri, bukan bagian di halaman depan ─────────────
 *
 * Sebagai bagian, ia berada lebih dari lima ribu piksel dari puncak halaman.
 * Yang sedang mencari cara menghubungi jarang menggulir sejauh itu; ia menyerah
 * lebih dulu, atau menyimpulkan tidak ada cara menghubungi sama sekali.
 *
 * Halaman tersendiri juga punya alamat yang bisa disebutkan. Itu penting saat
 * ada yang bertanya "di mana kontaknya" dan jawabannya harus bisa ditempel di
 * satu baris, termasuk kepada peninjau penyedia pembayaran.
 *
 * ── Kenapa formulir, bukan sekadar alamat email ──────────────────────────
 *
 * Menempel alamat email menuntut pengunjung berpindah ke aplikasi surat,
 * menyusun subjek sendiri, dan memikirkan apa yang perlu disebutkan. Formulir
 * yang sudah menanyakan namanya, emailnya, dan pesannya menghapus ketiganya.
 *
 * Kartu kontak tetap ditampilkan di sebelahnya. Sebagian orang memang lebih
 * suka menelepon atau menulis dari alamat emailnya sendiri, dan formulir yang
 * menjadi satu-satunya jalan justru mengusir mereka.
 */
export const KontakPage = () => {
  const { data } = useQuery({
    queryKey: ['site-content'],
    queryFn: () => api.get('/public/site-content'),
  });

  const konten = data?.data;
  const k = konten?.kontak ?? {};
  const email = k.email || konten?.contactEmail;

  const [form, setForm] = useState({
    nama: '',
    email: '',
    subjek: '',
    pesan: '',
    perangkap: '',
  });
  const [terkirim, setTerkirim] = useState(null);
  const set = (kunci) => (e) => setForm((s) => ({ ...s, [kunci]: e.target.value }));

  const kirim = useMutation({
    mutationFn: () => api.post('/public/kontak', form),
    onSuccess: (res) => {
      setTerkirim(res.data?.message ?? 'Pesan Anda sudah kami terima.');
      setForm({ nama: '', email: '', subjek: '', pesan: '', perangkap: '' });
    },
  });

  const siap =
    form.nama.trim().length >= 2 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()) &&
    form.pesan.trim().length >= 10;

  const cara = [
    email && { icon: Mail, label: 'Email', nilai: email, href: `mailto:${email}` },
    k.whatsapp && {
      icon: MessageCircle,
      label: 'WhatsApp',
      nilai: k.whatsapp,
      href: `https://wa.me/${String(k.whatsapp).replace(/\D/g, '')}`,
    },
    k.telepon && {
      icon: Phone,
      label: 'Telepon',
      nilai: k.telepon,
      href: `tel:${String(k.telepon).replace(/[^\d+]/g, '')}`,
    },
    k.alamat && { icon: MapPin, label: 'Alamat', nilai: k.alamat, href: null },
    k.jamOperasional && { icon: Clock, label: 'Jam operasional', nilai: k.jamOperasional, href: null },
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-[var(--color-void-950)] text-white">
      <div className="sticky top-0 z-50">
        <Nav />
      </div>

      <main className="mx-auto max-w-6xl px-5 pt-16 pb-24 sm:px-6 lg:pt-24">
        <p className="text-xs font-bold tracking-[0.16em] text-signal-400 uppercase">
          Hubungi Kami
        </p>
        <h1 className="display mt-4 max-w-2xl text-4xl font-extrabold sm:text-5xl">
          {k.judul || 'Ada yang ingin ditanyakan?'}
        </h1>
        <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-white/60">
          {k.pengantar ||
            'Tulis pesan Anda di bawah ini. Kami membalas lewat email pada hari kerja, biasanya dalam satu hari.'}
        </p>

        <div className="mt-12 grid gap-10 lg:grid-cols-[1.1fr_1fr] lg:gap-16">
          {/* ── Formulir ───────────────────────────────────────────── */}
          <div>
            {terkirim ? (
              <div className="rounded-2xl border border-signal-400/30 bg-signal-400/10 p-8">
                <Send className="size-6 text-signal-400" aria-hidden="true" />
                <h2 className="mt-4 text-xl font-extrabold">Terkirim</h2>
                <p className="mt-2 text-[15px] leading-relaxed text-white/70">{terkirim}</p>
                <button
                  type="button"
                  onClick={() => setTerkirim(null)}
                  className="mt-5 text-sm font-bold text-signal-400 underline underline-offset-4 hover:no-underline"
                >
                  Tulis pesan lain
                </button>
              </div>
            ) : (
              <form
                className="space-y-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (siap && !kirim.isPending) kirim.mutate();
                }}
              >
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Nama" required>
                    {(p) => (
                      <Input
                        {...p}
                        value={form.nama}
                        onChange={set('nama')}
                        placeholder="Nama Anda"
                        autoComplete="name"
                      />
                    )}
                  </Field>
                  <Field label="Email" required>
                    {(p) => (
                      <Input
                        {...p}
                        type="email"
                        value={form.email}
                        onChange={set('email')}
                        placeholder="nama@contoh.com"
                        autoComplete="email"
                      />
                    )}
                  </Field>
                </div>

                <Field label="Subjek" hint="Boleh dikosongkan.">
                  {(p) => (
                    <Input
                      {...p}
                      value={form.subjek}
                      onChange={set('subjek')}
                      placeholder="Pertanyaan soal paket Growth"
                    />
                  )}
                </Field>

                <Field label="Pesan" required hint="Minimal sepuluh huruf.">
                  {(p) => (
                    <Textarea
                      {...p}
                      rows={7}
                      value={form.pesan}
                      onChange={set('pesan')}
                      placeholder="Ceritakan apa yang ingin Anda kerjakan, atau apa yang sedang menghambat."
                    />
                  )}
                </Field>

                {/* Perangkap laba-laba.

                    Disembunyikan dari mata DAN dari pembaca layar, jadi tidak
                    ada orang yang pernah mengisinya. Program pengisi formulir
                    otomatis mengisi semua ruas yang ditemukannya, termasuk ini.

                    `tabIndex={-1}` penting: tanpa itu, pengguna keyboard bisa
                    tersasar ke kotak yang tidak terlihat dan mengetik di sana. */}
                <div aria-hidden="true" className="pointer-events-none absolute -left-[9999px]">
                  <label htmlFor="perangkap">Jangan diisi</label>
                  <input
                    id="perangkap"
                    name="perangkap"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.perangkap}
                    onChange={set('perangkap')}
                  />
                </div>

                {kirim.isError && (
                  <p role="alert" className="text-sm font-semibold text-danger-400">
                    {kirim.error instanceof ApiError
                      ? kirim.error.message
                      : 'Pesan gagal dikirim. Coba lagi sebentar lagi.'}
                  </p>
                )}

                <Button
                  type="submit"
                  icon={Send}
                  loading={kirim.isPending}
                  disabled={!siap}
                  className="w-full sm:w-auto"
                >
                  Kirim Pesan
                </Button>
              </form>
            )}
          </div>

          {/* ── Kartu kontak ───────────────────────────────────────── */}
          <div>
            <p className="text-xs font-bold tracking-[0.14em] text-white/50 uppercase">
              Atau langsung
            </p>

            <div className="mt-4 space-y-2.5">
              {cara.map((c) => {
                const isi = (
                  <>
                    <c.icon className="mt-0.5 size-[18px] shrink-0 text-signal-400" aria-hidden="true" />
                    <span className="min-w-0">
                      <span className="block text-xs font-bold tracking-wide text-white/40 uppercase">
                        {c.label}
                      </span>
                      <span className="mt-0.5 block text-sm leading-snug font-semibold break-words text-white">
                        {c.nilai}
                      </span>
                    </span>
                  </>
                );

                return c.href ? (
                  <a
                    key={c.label}
                    href={c.href}
                    {...(c.href.startsWith('http') ? { target: '_blank', rel: 'noreferrer noopener' } : {})}
                    className="flex gap-3 rounded-xl border border-white/8 bg-white/[0.03] p-4 transition-colors hover:border-signal-400/40 hover:bg-white/[0.06]"
                  >
                    {isi}
                  </a>
                ) : (
                  <div
                    key={c.label}
                    className="flex gap-3 rounded-xl border border-white/8 bg-white/[0.03] p-4"
                  >
                    {isi}
                  </div>
                );
              })}
            </div>

            {k.namaUsaha && (
              <p className="mt-5 text-xs leading-relaxed text-white/35">
                Layanan ini dijalankan oleh {k.namaUsaha}.
              </p>
            )}
          </div>
        </div>
      </main>

      <Footer kontak={konten?.contactEmail} konten={konten} />
    </div>
  );
};
